/**
 * Guards Response.json() against empty bodies and non-OK statuses before parsing.
 * Used by Amplify/Cognito token refresh (e.g. 504 gateway with empty body).
 */
export function parseResponseJsonSafely(response: Response): Promise<unknown> {
	return response.text().then(text => {
		const trimmed = text.trim();
		if (!trimmed) {
			if (!response.ok) {
				throw new Error(
					`HTTP request failed with status ${response.status}`
				);
			}
			throw new Error('Empty response body');
		}
		try {
			return JSON.parse(trimmed) as unknown;
		} catch (error) {
			if (!response.ok) {
				throw new Error(
					`HTTP request failed with status ${response.status}`
				);
			}
			throw error;
		}
	});
}

function patchResponseJson(response: Response): Response {
	const responseWithSafeJson = response as Response & {
		json: () => Promise<unknown>;
	};
	responseWithSafeJson.json = () => parseResponseJsonSafely(response);
	return responseWithSafeJson;
}

/**
 * Install a global fetch wrapper so Amplify/Cognito calls check response.ok and
 * empty bodies before JSON parsing. Call once before Amplify.configure().
 */
export function installSafeAmplifyFetch(): void {
	if (typeof globalThis.fetch !== 'function') {
		return;
	}
	const originalFetch = globalThis.fetch.bind(globalThis);
	if ((originalFetch as { __jeptoSafeFetch?: boolean }).__jeptoSafeFetch) {
		return;
	}

	const safeFetch: typeof fetch = async (input, init) => {
		const response = await originalFetch(input, init);
		return patchResponseJson(response);
	};
	(safeFetch as { __jeptoSafeFetch?: boolean }).__jeptoSafeFetch = true;

	globalThis.fetch = safeFetch;
}
